import React, { useEffect, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import ProductCard from './ProductCard';
import Loader from '../Loader';
import useStore from '../../store/useStore';
import { buildApiUrl } from '../../utils/api';
import { normalizeProductCard } from '../../utils/normalizeProductCard';
import { getActivePincode } from '../../utils/location';

const RelatedProductsSection = ({ productId, categoryId, categoryName }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const activePincode = useStore((s) => getActivePincode(s));

  useEffect(() => {
    let alive = true;

    const fetchRelatedProducts = async () => {
      if (!productId) return;

      setLoading(true);
      try {
        const params = new URLSearchParams({ limit: '12' });
        if (activePincode) params.set('pincode', String(activePincode));
        const response = await fetch(buildApiUrl(`/api/v1/customer/products/related/${productId}?${params.toString()}`), {
          headers: { 'Content-Type': 'application/json' },
        });
        const json = await response.json();
        if (!response.ok || !json.success) throw new Error(json.message || 'Failed to load related products');

        const related = (json.data || [])
          .filter((item) => String(item.product_id ?? item.id) !== String(productId))
          .map(normalizeProductCard);

        if (alive) setProducts(related);
      } catch {
        if (alive) setProducts([]);
      } finally {
        if (alive) setLoading(false);
      }
    };

    fetchRelatedProducts();
    return () => { alive = false; };
  }, [productId, activePincode]);

  if (!loading && products.length === 0) return null;

  return (
    <section className="mt-12 overflow-hidden">
      {/* Header */}
      <div className="mb-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          {/* Accent bar */}
          <div className="h-8 w-1 rounded-full bg-primary-500" />
          <div>
            <span className="block text-[11px] font-bold uppercase tracking-[0.2em] text-primary-600">
              🌱 You May Also Like
            </span>
            <h2 className="text-xl font-black tracking-tight text-gray-900 sm:text-2xl">
              Related Products
            </h2>
          </div>
        </div>

      </div>

      {/* Content */}
      {loading ? (
        <div className="rounded-3xl border border-gray-100 bg-white">
          <Loader compact label="Loading related products" sublabel="Finding similar fresh picks." />
        </div>
      ) : (
        <div className="overflow-hidden">
          <Swiper
            modules={[Autoplay, Navigation]}
            spaceBetween={12}
            slidesPerView={2}
            autoplay={{ delay: 3000, disableOnInteraction: false }}
            navigation
            className="!overflow-hidden !pb-1"
            breakpoints={{
              480:  { slidesPerView: 2, spaceBetween: 12 },
              640:  { slidesPerView: 3, spaceBetween: 14 },
              768:  { slidesPerView: 4, spaceBetween: 14 },
              1024: { slidesPerView: 4, spaceBetween: 16 },
              1280: { slidesPerView: 5, spaceBetween: 16 },
            }}
          >
            {products.map((item) => (
              <SwiperSlide key={item.product_id} className="!h-auto">
                <ProductCard product={item} />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      )}
    </section>
  );
};

export default RelatedProductsSection;
